import { FLAGS } from '@iheartjane/feature-flags';
import { WizardButton } from '@iheartjane/menu';
import { registerJdid as registerJdidToRum } from '@iheartjane/tracking';
import { useEffect } from 'react';
import { useLoaderData } from 'react-router';

import { BloomWizardModal } from '@jane/bloom/components/bloomWizardModal';
import {
  FilterSelector,
  MobileFilterRow,
} from '@jane/bloom/components/productListingPage/categoryAndFilterSelectors';
import { SeoHeading } from '@jane/bloom/components/seoHeading';
import { useMemoizedRowInstance } from '@jane/bloom/hooks/dm/useMemoizedRowInstance';
import { useMenuTopRowWithServerData } from '@jane/bloom/hooks/dm/useMenuTopRowWithServerData';
import { BloomFilterProvider } from '@jane/bloom/hooks/filters';
import { useRootLoaderData } from '@jane/bloom/hooks/useRootLoaderData';
import { useShowInlineWizardCta } from '@jane/bloom/hooks/useShowInlineWizardCta';
import type { RecommendedSortResults } from '@jane/bloom/loaders/products/recommendedSortResults.server';
import { createMetaVariables } from '@jane/bloom/resources/meta/createMetaVariables';
import {
  DEFAULT_CATEGORY_TITLE,
  DEFAULT_SHOP_ALL_TITLE,
} from '@jane/bloom/resources/meta/routes/category';
import { WIZARD_SOURCE } from '@jane/bloom/services/tracking/Events';
import { trackSearchedMenuProducts } from '@jane/bloom/services/tracking/track';

import { CategoryTab } from './components/categoryTab';

export function CategoryRouteComponent() {
  const { cart, customSEO, featureFlags, metaProps, pinnedStore } =
    useRootLoaderData();
  const showInlineWizardCta = useShowInlineWizardCta();
  const {
    availableFilters,
    brandSpecials,
    category,
    customCategoryLabels,
    customLanguage,
    dynamicPlacement: serializedDynamicPlacement,
    enabledSpecialsForRow: enabledStoreSpecials,
    janeGold,
    jdid,
    menuProductImgsAboveTheFold,
    menuTopRow,
    recommendedSortResults,
    searchCriteria,
    searchFilterWithoutBrands,
    sort: { currentSort, sortOptions },
    storeId,
  } = useLoaderData<RecommendedSortResults>();

  useEffect(() => {
    registerJdidToRum(jdid);
  }, [jdid]);

  const rowInstance = useMemoizedRowInstance(recommendedSortResults);
  const dynamicPlacement = useMemoizedRowInstance(serializedDynamicPlacement);

  // Handles server-side initial load and client-side refetching of the menu top row
  const {
    pauseRefetching,
    rowInstance: menuTopRowInstance,
    unpauseRefetching,
  } = useMenuTopRowWithServerData({
    initialMenuTopRow: menuTopRow,
    searchFilter: searchFilterWithoutBrands,
    storeId,
  });

  useEffect(() => {
    if (!rowInstance) return;
    trackSearchedMenuProducts({
      hits: rowInstance?.totalProducts,
      searchCriteria,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(searchCriteria), rowInstance?.totalProducts]);

  const metaVariables = createMetaVariables(metaProps, {
    tabName: category ? `${customCategoryLabels?.[category]}` : 'all',
  });

  return (
    <BloomFilterProvider
      availableFilters={availableFilters}
      searchCriteria={searchCriteria}
    >
      <SeoHeading
        customSEOPage={
          category ? (customSEO?.category ?? {}) : (customSEO?.shopAll ?? {})
        }
        defaultTitle={
          category ? DEFAULT_CATEGORY_TITLE : DEFAULT_SHOP_ALL_TITLE
        }
        metaVariables={metaVariables}
      />

      <BloomWizardModal
        brandSpecials={brandSpecials}
        specials={enabledStoreSpecials}
      >
        {({ openModal }) => (
          <>
            <div className="filters-container">
              <FilterSelector
                actionSlot={
                  showInlineWizardCta && category ? (
                    <WizardButton
                      onClickStart={() => openModal(WIZARD_SOURCE.BUTTON)}
                    />
                  ) : undefined
                }
                availableProductsCount={rowInstance?.totalProducts}
                sortOptions={sortOptions}
                currentSort={currentSort}
                shouldShowGold={janeGold.shouldShowGold}
                hasJaneGoldProducts={janeGold.hasJaneGoldProducts}
                showCompare={featureFlags[FLAGS.c1CompareProducts]}
              />
            </div>

            <MobileFilterRow
              actionSlot={
                showInlineWizardCta && category ? (
                  <WizardButton
                    onClickStart={() => openModal(WIZARD_SOURCE.BUTTON)}
                  />
                ) : undefined
              }
              availableProductsCount={rowInstance?.totalProducts}
              sortOptions={sortOptions}
              currentSort={currentSort}
              shouldShowGold={janeGold.shouldShowGold}
              hasJaneGoldProducts={janeGold.hasJaneGoldProducts}
              showCompare={featureFlags[FLAGS.c1CompareProducts]}
            />

            <CategoryTab
              pauseRefetching={pauseRefetching}
              unpauseRefetching={unpauseRefetching}
              brandSpecials={brandSpecials}
              cart={cart}
              category={category}
              customCategoryLabels={customCategoryLabels}
              customLanguage={customLanguage}
              dynamicPlacement={dynamicPlacement}
              menuTopRowInstance={menuTopRowInstance}
              openWizard={openModal}
              preloadedProductIds={menuProductImgsAboveTheFold.map(
                ({ id }) => id
              )}
              rowInstance={rowInstance}
              specials={enabledStoreSpecials}
              janeGold={janeGold}
              pinnedStore={pinnedStore}
              enabledSpecialsForRow={enabledStoreSpecials}
            />
          </>
        )}
      </BloomWizardModal>
    </BloomFilterProvider>
  );
}
